import { z } from 'zod';
import {
  KPI_AGGREGATIONS,
  type KpiAggregationName,
  type KpiDirectionName,
  type KpiPeriodicityName,
} from './kpis';
import { kpiPerformance } from './performance';
import { periodMonthsOf, projectClosing, PROJECTION_METHODS, LINEAR_WINDOW } from './planned';
import { TRAFFIC_LIGHTS, type TrafficLight } from './status';

// ---------------------------------------------------------------------------------------------
// Histórico do indicador (T-069, RF-27, RN-05, PRD §11.15; DEC-039)

export const HISTORY_WINDOWS = [12, 24, 36] as const;
export type HistoryWindow = (typeof HISTORY_WINDOWS)[number];

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
export const historyPointLabel = (year: number, month: number) => `${MONTHS[month - 1]}/${year}`;

const light = z.enum(TRAFFIC_LIGHTS);
export const historyPointSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  label: z.string(),
  actual: z.number().nullable(),
  target: z.number().nullable(),
  performance: z.number().nullable(),
  light,
  ytdActual: z.number().nullable(),
  ytdTarget: z.number().nullable(),
  ytdPerformance: z.number().nullable(),
  ytdLight: light,
});
export type HistoryPoint = z.infer<typeof historyPointSchema>;

export const TREND_DIRECTIONS = ['UP', 'DOWN', 'STABLE'] as const;
export type TrendDirectionName = (typeof TREND_DIRECTIONS)[number];
export const TREND_LABELS: Record<TrendDirectionName, string> = {
  UP: 'Subindo',
  DOWN: 'Caindo',
  STABLE: 'Estável',
};
/** Variação por período abaixo deste percentual da média = estável. */
export const TREND_STABLE_PERCENT = 1;

const periodSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  label: z.string(),
  actual: z.number().nullable(),
  target: z.number().nullable(),
  performance: z.number().nullable(),
  light,
});

export const historyStatsSchema = z.object({
  /** Último período com realizado. */
  current: periodSchema.nullable(),
  /** Meta do período em andamento (ou, sem ela, a última já vencida). */
  currentTarget: z
    .object({ year: z.number().int(), month: z.number().int(), label: z.string(), value: z.number() })
    .nullable(),
  /** Média simples dos realizados da janela. */
  average: z.number().nullable(),
  /** Acumulado do ano corrente até o último realizado. */
  ytd: z
    .object({
      year: z.number().int(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      performance: z.number().nullable(),
      light,
    })
    .nullable(),
  best: periodSchema.nullable(),
  worst: periodSchema.nullable(),
  /** Tendência dos últimos até 6 períodos com realizado (favorável pela direção; faixa e fórmula: null). */
  trend: z
    .object({
      direction: z.enum(TREND_DIRECTIONS),
      slope: z.number(),
      favorable: z.boolean().nullable(),
    })
    .nullable(),
  /** Projeção de fechamento do ano corrente (os métodos da DEC-038). */
  projections: z.array(
    z.object({
      method: z.enum(PROJECTION_METHODS),
      closing: z.number().nullable(),
      closingPerformance: z.number().nullable(),
    }),
  ),
});
export type HistoryStats = z.infer<typeof historyStatsSchema>;

export type HistoryKpi = {
  direction: KpiDirectionName;
  aggregation: KpiAggregationName;
  periodicity: KpiPeriodicityName;
  rangeLower: number | null;
  rangeUpper: number | null;
  performanceFormula: string | null;
};

const r6 = (v: number | null) => (v === null ? null : Math.round(v * 1e6) / 1e6);
const period = (p: HistoryPoint) => ({
  year: p.year,
  month: p.month,
  label: p.label,
  actual: p.actual,
  target: p.target,
  performance: p.performance,
  light: p.light as TrafficLight,
});
const key = (p: { year: number; month: number }) => p.year * 12 + p.month;

/**
 * Tendência dos realizados (em ordem): inclinação da reta dos últimos `LINEAR_WINDOW` períodos; estável abaixo de
 * `TREND_STABLE_PERCENT`% da média; favorável conforme o sentido do KPI. Também usada no retrato da pauta da
 * análise crítica (DEC-050).
 */
export function kpiTrend(actuals: number[], direction: HistoryKpi['direction']): HistoryStats['trend'] {
  const window = actuals.slice(-LINEAR_WINDOW);
  if (window.length < 2) return null;
  const n = window.length;
  const mx = (n - 1) / 2;
  const my = window.reduce((s, v) => s + v, 0) / n;
  const sxx = window.reduce((s, _v, i) => s + (i - mx) ** 2, 0);
  const slope = window.reduce((s, v, i) => s + (i - mx) * (v - my), 0) / sxx;
  const relative = my === 0 ? (slope === 0 ? 0 : Infinity) : Math.abs(slope / my) * 100;
  const way: TrendDirectionName = relative < TREND_STABLE_PERCENT ? 'STABLE' : slope > 0 ? 'UP' : 'DOWN';
  const favorable =
    way === 'STABLE'
      ? null
      : direction === 'HIGHER_BETTER'
        ? way === 'UP'
        : direction === 'LOWER_BETTER'
          ? way === 'DOWN'
          : null;
  return { direction: way, slope: r6(slope)!, favorable };
}

/**
 * Indicadores do topo do histórico (PRD §11.15) a partir da série da janela (em ordem) e dos 12 meses do ano
 * corrente: resultado e meta atuais, média, acumulado, melhor e pior período (pela performance), tendência e
 * projeção de fechamento.
 */
export function kpiHistoryStats(input: {
  points: HistoryPoint[];
  yearPoints: HistoryPoint[];
  kpi: HistoryKpi;
  cap: number;
  today: { year: number; month: number };
}): HistoryStats {
  const { points, yearPoints, kpi, cap, today } = input;
  const realized = points.filter((p) => p.actual !== null);
  const current = realized.at(-1) ?? null;
  // Meta atual: a do período em andamento (o primeiro mês de fechamento a partir de hoje — no anual,
  // dezembro do ano corrente); sem meta nele, a última meta já vencida da janela.
  const inProgress = periodMonthsOf(kpi.periodicity).find((m) => m >= today.month) ?? 12;
  const progressPoint = yearPoints.find((p) => p.month === inProgress && p.target !== null) ?? null;
  const targetPoint =
    progressPoint ?? points.filter((p) => p.target !== null && key(p) <= key(today)).at(-1) ?? null;
  const ranked = realized.filter((p) => p.performance !== null);
  // Empate: o mais recente.
  const best = ranked.reduce<HistoryPoint | null>(
    (a, p) => (!a || p.performance! >= a.performance! ? p : a),
    null,
  );
  const worst = ranked.reduce<HistoryPoint | null>(
    (a, p) => (!a || p.performance! <= a.performance! ? p : a),
    null,
  );
  const average = realized.length ? r6(realized.reduce((s, p) => s + p.actual!, 0) / realized.length) : null;

  const trend = kpiTrend(
    realized.map((p) => p.actual!),
    kpi.direction,
  );

  const yearRealized = yearPoints.filter((p) => p.actual !== null);
  const lastYear = yearRealized.at(-1);
  const ytd = lastYear
    ? {
        year: lastYear.year,
        actual: lastYear.ytdActual,
        target: lastYear.ytdTarget,
        performance: lastYear.ytdPerformance,
        light: lastYear.ytdLight as TrafficLight,
      }
    : null;
  const months = periodMonthsOf(kpi.periodicity);
  const actuals = Array.from(
    { length: 12 },
    (_, i) => yearPoints.find((p) => p.month === i + 1)?.actual ?? null,
  );
  const annualTarget = yearPoints.find((p) => p.month === 12)?.ytdTarget ?? null;
  const perf = {
    direction: kpi.direction,
    rangeLower: kpi.rangeLower,
    rangeUpper: kpi.rangeUpper,
    performanceFormula: kpi.performanceFormula,
  };
  const projections = PROJECTION_METHODS.map((method) => {
    const closing = projectClosing(actuals, kpi.aggregation, method, months).closing;
    return {
      method,
      closing,
      closingPerformance: kpiPerformance({ ...perf, actual: closing, target: annualTarget }, cap),
    };
  });

  return {
    current: current ? period(current) : null,
    currentTarget: targetPoint
      ? {
          year: targetPoint.year,
          month: targetPoint.month,
          label: targetPoint.label,
          value: targetPoint.target!,
        }
      : null,
    average,
    ytd,
    best: best ? period(best) : null,
    worst: worst ? period(worst) : null,
    trend,
    projections,
  };
}

// --- Contrato da API ---------------------------------------------------------------------------

export const kpiHistoryQuerySchema = z.object({
  months: z.coerce
    .number()
    .int()
    .refine((v) => (HISTORY_WINDOWS as readonly number[]).includes(v), 'Escolha 12, 24 ou 36 meses.')
    .default(12),
});

export const kpiHistorySchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    periodicity: z.string(),
    direction: z.string(),
    aggregation: z.enum(KPI_AGGREGATIONS),
  }),
  window: z.object({
    months: z.number().int(),
    from: z.object({ year: z.number().int(), month: z.number().int() }),
    to: z.object({ year: z.number().int(), month: z.number().int() }),
  }),
  /** Só os meses em que o período do KPI fecha (trimestral: 4 por ano). */
  points: z.array(historyPointSchema),
  stats: historyStatsSchema,
  /** Quando os agregados foram calculados pela última vez. */
  computedAt: z.string().nullable(),
});
export type KpiHistory = z.infer<typeof kpiHistorySchema>;

/** Uma alteração do KPI (cadastro, meta ou resultado) vinda da trilha de auditoria. */
export const kpiChangeSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actorName: z.string().nullable(),
  /** kpi (cadastro), kpi_target (meta) ou kpi_result (realizado). */
  entity: z.string(),
  action: z.string(),
  field: z.string().nullable(),
  /** Nome do campo e os valores como a pessoa lê (rótulos, nomes no lugar de ids, datas e números). */
  fieldLabel: z.string().nullable(),
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  oldDisplay: z.string().nullable(),
  newDisplay: z.string().nullable(),
  source: z.string(),
  reason: z.string().nullable(),
  /** Período e unidade da meta/resultado e marcas da regra (ex.: período encerrado). */
  periodStart: z.string().nullable(),
  periodType: z.string().nullable(),
  year: z.number().int().nullable(),
  period: z.number().int().nullable(),
  businessUnitId: z.string().nullable(),
  /** Unidade da meta ou do resultado ("Consolidado" no geral; null no cadastro). */
  unitName: z.string().nullable(),
  /** Período da meta ou do resultado em português ("Mar/2026", "2º tri/2026", "Ano 2026"); null no cadastro. */
  periodLabel: z.string().nullable(),
  closedPeriod: z.boolean(),
});
export type KpiChange = z.infer<typeof kpiChangeSchema>;

export const kpiChangesQuerySchema = z.object({
  /** Cursor: "occurredAt|id" do último item da página anterior. */
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const kpiChangesSchema = z.object({
  items: z.array(kpiChangeSchema),
  nextCursor: z.string().nullable(),
});
